import React, { useCallback, useState } from 'react';
import { StandardModal } from 'pureUi/Modal';
import { CloseBoldIcon } from 'ui/Icons/components/CloseBold.component';
import { theme } from '../../../tailwind.config';
import { TextInput } from 'ui/TextInput';
import FluidButton from 'ui/FluidButton';
import { isValidEmail } from 'utils/email';
import { useVouchersV2AccessListQueries } from 'containers/BookingManagerVouchersV2/useVouchersV2AccessQueries';
import { makeItineraryManagerApi } from 'services/ItineraryManagerApi';

export interface IAuthorizeUserModalProps {
  className?: string;
  onClose: () => void;
  voucherUuid: string;
}

export const AuthorizeUserModal: React.FC<IAuthorizeUserModalProps> = ({
  onClose, voucherUuid
}) => {
  const [fields, setFields] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string | null }>({});

  const vouchersV2AccessList = useVouchersV2AccessListQueries(
    makeItineraryManagerApi(),
    voucherUuid
  );

  const handleFieldChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFields(prev => ({ ...prev, [id]: value }));
    setFieldErrors(prev => ({ ...prev, [id]: null }));
  }, [setFields, setFieldErrors]);

  const validateFields = useCallback(() => {
    const errs: { [key: string]: string | null } = {};
    Object.entries(fields).forEach(([key, value]) => {
      if (!value?.trim()) {
        errs[key] = 'Field required';
      } else if (key === 'email' && !isValidEmail(value.trim())) {
        errs[key] = 'Must be valid email';
      } else {
        errs[key] = null;
      }
    });
    setFieldErrors(errs);
    return Object.values(errs).every(error => !error);
  }, [fieldErrors, setFieldErrors]);

  const handleShareExternalUser = useCallback(async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!validateFields()) {
      return;
    }
    const { firstName, lastName, email } = fields;
    if (!firstName || !lastName || !email) {
      return;
    }
    await vouchersV2AccessList.addToVoucherV2AccessList.mutateAsync({
      voucherUuid,
      firstName,
      lastName,
      email,
    });
    onClose();
  }, [voucherUuid, fields, vouchersV2AccessList]);

  return (
    <StandardModal
      frameClassName="w-[520px] min-h-[360px] max-h-[386px] px-[35px] py-[25px]"
      className="transparent z-[9999]"
      onClose={onClose}
      focusTrapActive={false}
      showCloseButton={false}
      removePadding
    >
      <div className="flex flex-col gap-[25px]">
        <div className="modal-heading-container flex items-center justify-between gap-[20px]">
          <h2 className="modal-heading m-0 font-noe-display text-xl font-light leading-[27px] text-black">
            External Authorized Viewers
          </h2>
          <CloseBoldIcon onClick={onClose} className="cursor-pointer min-w-[12px] min-h-[12px]" fill={theme.colors['brown-100']} />
        </div>
        <div className='text-black font-pt-sans'>
          Share with external user who need access to this document.
        </div>
        <div className='flex gap-5 items-start'>
          <TextInput
            id='firstName'
            placeholder='Enter first name'
            value={fields.firstName}
            onChange={handleFieldChange}
            type="text"
            className='w-[250px]'
            label='First Name *'
            required
            disabled={vouchersV2AccessList.addToVoucherV2AccessList.isPending}
            errorMessage={fieldErrors.firstName}
            labelClassName='font-bold'
          />
          <TextInput
            id='lastName'
            placeholder='Enter last name'
            value={fields.lastName}
            onChange={handleFieldChange}
            type="text"
            className='w-[250px]'
            label='Last Name *'
            required
            disabled={vouchersV2AccessList.addToVoucherV2AccessList.isPending}
            errorMessage={fieldErrors.lastName}
            labelClassName='font-bold'
          />
        </div>
        <TextInput
          id='email'
          placeholder='Enter email address'
          value={fields.email}
          onChange={handleFieldChange}
          type="email"
          className='w-full'
          label='Email Address *'
          required
          disabled={vouchersV2AccessList.addToVoucherV2AccessList.isPending}
          errorMessage={fieldErrors.email}
          labelClassName='font-bold'
        />
        <div className='flex justify-start gap-[10px]'>
          <FluidButton
            fixedWidth='67px'
            type='secondary'
            className='min-w-[67px]'
            disabled={vouchersV2AccessList.addToVoucherV2AccessList.isPending}
            onClick={onClose}
          >
            Cancel
          </FluidButton>
          <FluidButton
            fixedWidth='67px'
            type='primary'
            className='min-w-[67px]'
            isLoading={vouchersV2AccessList.addToVoucherV2AccessList.isPending}
            onClick={handleShareExternalUser}
          >
            Share
          </FluidButton>
        </div>
      </div>
    </StandardModal>
  )
}